import React, { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Button, IconButton, Spinner, Typography, Tooltip } from "@material-tailwind/react";
import { runInAction } from 'mobx';
import { observer } from "mobx-react-lite";
import { motion, AnimatePresence } from "framer-motion";
import { XMarkIcon } from "@heroicons/react/24/solid";
import { MapPinIcon } from "@heroicons/react/20/solid";
import { InformationCircleIcon, TrashIcon } from "@heroicons/react/24/outline";

import { DrawerBase } from "./DrawerBase";
import { stockiestPortalController } from "../../../../Common/Controllers/StockiestPortalController";
import { StockistBasketItemDTO } from "../../../../Common/DTOs/Basket/StockistBasketItemDTO";
import { alertController } from "../../../../Common/Controllers/AlertController";
import { userAccountController } from "../../../../Common/Controllers/UserAccountController";
import { issueDetailsController } from "../../Controllers/IssueDetailsController";

export const BasketDrawer = observer(() => {
  const navigate = useNavigate();

  const placeOrder = async () => {
    if (!userAccountController.isLoggedIn) {
      alertController.displayAlert(
        "Login required",
        "You have to be logged in in order to view this page.",
        "warning",
        4000
      )
      return
    }

    // get api user so we always have the most recent isActive status
    await userAccountController.getApiUser(userAccountController.currentUser?.id!);

    if (!userAccountController.isActivated) {
      alertController.displayAlert(
        "Activation required",
        "Your account has not been activated. Please contact Newsstand in order to buy magazines.",
        "warning",
        4000
      )
      return
    }

    runInAction(() => stockiestPortalController.basketDrawerOpen = false);
    navigate("/checkout")
  }

  useEffect(() => {
    if (stockiestPortalController.basketDrawerOpen) {
      document.documentElement.style.overflowX = "";
      document.body.style.overflowY = "hidden";
      document.body.style.paddingRight = "10px";
    } else {
      document.documentElement.style.overflowX = "hidden";
      document.body.style.overflowY = "";
      document.body.style.paddingRight = "";
    }

    return () => {
      document.documentElement.style.overflowX = "hidden";
      document.body.style.overflowY = "";
      document.body.style.paddingRight = "";
    };
  }, [stockiestPortalController.basketDrawerOpen]);

  return (
    <DrawerBase
      isOpen={stockiestPortalController.basketDrawerOpen}
      closeDrawer={() =>
        stockiestPortalController.toggleBasketDrawerOpen(false)
      }
    >
      <div className="relative h-full">
        <div
          className={`absolute w-full flex flex-col items-center justify-center h-full transition duration-300 ease-in-out ${stockiestPortalController.isBasketDataLoading ? "opacity-100" : "opacity-0"}`}>
          <Spinner className="h-16 w-16 text-white"/>
          <p className="mt-4 text-white text-lg animate-pulse">Loading basket...</p>
        </div>

        <div
          className={`absolute flex flex-col h-full w-full transition duration-300 ease-in-out ${stockiestPortalController.isBasketDataLoading ? "opacity-10" : "opacity-100"}`}>
          <div className="flex items-center justify-between animate-fadeIn flex-none">
            <div className="flex flex-col gap-1 p-2">
              <Typography variant="h4" color="white">
                Basket Summary
              </Typography>
              <Typography variant="h6" color="white">
                £{stockiestPortalController.totalPriceInclDiscountInPounds?.toFixed(2)}
              </Typography>
            </div>

            <IconButton
              variant="text"
              color="blue-gray"
              onClick={() =>
                stockiestPortalController.toggleBasketDrawerOpen(false)
              }
            >
              <XMarkIcon className="h-6 w-6 text-white"/>
            </IconButton>
          </div>

          <div className="border-t border-gray-600/90 flex-auto flex flex-col h-full overflow-hidden">
            {stockiestPortalController.purchasableOrUpcomingOrderedProductsPresent ? (
              <>
                <div className="flex flex-col gap-1 animate-fadeIn flex-none border-b-2">
                  <div className="flex flex-row gap-1 px-2 pt-2">
                    <Typography variant="h6" color="white" className="basis-3/5">
                      Magazine cost:
                    </Typography>
                    <Typography variant="h6" color="white" className="basis-2/5">
                      £{stockiestPortalController.totalIssuePrice?.toFixed(2)}
                    </Typography>
                  </div>

                  {!!stockiestPortalController.totalDiscountAmount &&
                    stockiestPortalController.totalDiscountAmount > 0 && (
                      <div className="flex flex-row gap-1 px-2">
                        <Typography
                          variant="h6"
                          color="white"
                          className="basis-3/5 text-green-500"
                        >
                          Discounts:
                        </Typography>
                        <Typography
                          variant="h6"
                          color="white"
                          className="basis-2/5 text-green-500"
                        >
                          -£{stockiestPortalController.totalDiscountAmount.toFixed(2)}
                        </Typography>
                      </div>
                    )}

                  <div className="flex flex-row gap-1 p-2">
                    <Typography variant="h6" color="white" className="basis-3/5">
                      Order Value:
                    </Typography>
                    <Typography variant="h6" color="white" className="basis-2/5">
                      £{stockiestPortalController.totalPriceInclDiscountInPounds?.toFixed(2)}
                    </Typography>
                  </div>

                  {!!stockiestPortalController.deliveryFrom &&
                    stockiestPortalController.deliveryFrom > 0 && (
                      <div className="flex flex-col px-2 mb-1 animate-fadeIn">
                        <div className="justify-self-end flex flex-row gap-1 mt-6">
                          <div className="flex flex-nowrap gap-1">
                            <Typography
                              variant="small"
                              className="text-light-grey/80 text-sm italic"
                            >
                              Delivery from{" "}
                              <span className="font-bold">
                                £{stockiestPortalController.deliveryFrom?.toFixed(2)}
                              </span>
                            </Typography>
                            {stockiestPortalController.deliveryAddress && (
                              <div
                                className="inline-flex items-center bg-light-grey/10 rounded px-1 w-max text-xs text-light-grey gap-0.5">
                                <MapPinIcon className="h-3 w-3"/>
                                <span>
                                  {stockiestPortalController.deliveryAddress.postCode}{" "}
                                  {stockiestPortalController.deliveryAddress.country}
                                </span>
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                    )
                  }
                </div>
                <div className="flex-auto overflow-y-auto">
                  {stockiestPortalController.purchasableOrderedProducts.map((op) => (
                    <BasketItem orderedProduct={op} key={op.issue.id}/>
                  ))}

                  {stockiestPortalController.upcomingOrderedProducts.length > 0 && (
                    <div className="border-t-2 pt-3 bg-main">
                      <div className="mb-2 flex flex-col">
                        <Typography
                          variant="h5"
                          className="px-2"
                        >
                          Upcoming issues
                        </Typography>
                        <span className="text-xs text-gray-300/80 italic font-light pl-2">
                          They won’t be added to your order
                        </span>
                      </div>
                      <div className="flex flex-col">
                        {stockiestPortalController.upcomingOrderedProducts.map((op) => (
                          <BasketItem orderedProduct={op} key={op.issue.id}/>
                        ))}
                      </div>
                    </div>
                  )}
                </div>

                <div className="border-t-2 animate-fadeUpIn flex-none">
                  <Button
                    className="w-full rounded-none bg-purple hover:bg-purple/80"
                    size="lg"
                    onClick={placeOrder}
                    disabled={!stockiestPortalController.purchasableOrderedProducts.length}
                  >
                    Place order
                  </Button>
                </div>
              </>
            ) : (
              <Typography variant="h6" color="white" className="px-2 mt-4">
                There are currently no items in your basket
              </Typography>
            )}
          </div>
        </div>
      </div>
    </DrawerBase>
  );
})

type BasketItemProps = {
  orderedProduct: StockistBasketItemDTO
}

export const BasketItem: React.FC<BasketItemProps> = observer(({orderedProduct}) => {
  const navigate = useNavigate();
  const {issue, quantity} = orderedProduct;
  const {mostSpecificDiscounts, stock} = issue;
  const [inputValue, setInputValue] = useState(quantity.toString());
  const sortedDiscounts = useMemo(
    () => mostSpecificDiscounts?.slice().sort((a, b) => a.quantityFrom - b.quantityFrom),
    [mostSpecificDiscounts]
  );

  const baseTotal = orderedProduct.totalIssuePrice;
  const discountedTotal = orderedProduct.totalDiscountedIssuePrice;
  const discountPercentage = orderedProduct.issueDiscountPercentage;

  const changeOrderAmount = (deltaOrNewValue: number, isDelta = true) => {
    if (isNaN(deltaOrNewValue) || (!isDelta && deltaOrNewValue < 0))
      return;

    let newQuantity = isDelta
      ? quantity + deltaOrNewValue
      : deltaOrNewValue;

    newQuantity = Math.max(0, newQuantity);
    stockiestPortalController.updateOrderedProduct(orderedProduct, newQuantity);
  };

  const removeItem = () => {
    stockiestPortalController.removeOrderedProduct(orderedProduct);
  };

  useEffect(() => {
    setInputValue(quantity.toString());
  }, [orderedProduct, quantity]);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const val = e.target.value;

    if (val === "") {
      setInputValue("");
      return;
    }

    const num = parseInt(val);
    if (isNaN(num)) return;

    setInputValue(Math.max(1, Math.min(num, orderedProduct.maxAvailableQuantity)).toString());
  };

  const handleBlur = () => {
    if (inputValue === "") {
      // Note: revert to original value if left empty
      setInputValue(quantity.toString());
      return;
    }

    const num = parseInt(inputValue);
    if (!isNaN(num) && num !== quantity) {
      changeOrderAmount(num, false);
    }
  };

  return (
    <div
      className="bg-dark-grey border-y first:border-t-0 last:border-b-0 border-light-grey/30 py-2 flex justify-between items-center gap-4 transition-all shadow-md px-2 relative">
      <div className={`flex flex-col items-center relative ${(!issue.imageAvailable) ? "gap-4" : ""}`}>
        <div
          className="w-[120px] h-[150px] flex-shrink-0 relative hover:shadow-[0_0_12px_#fff] hover:shadow-white transition ease-in-out">
          {issue.imageAvailable ? (
            <img
              src={issue.imageURL_230x320 || "https://www.newsstand.co.uk/images/coming_soon_230x320.jpg"}
              alt={issue.identity}
              className={`w-full h-full outline outline-1 outline-white object-cover rounded-md shadow-sm cursor-pointer ${(issue.issueOnSale <= new Date() && stock.stockAvailableForStockist <= 0) ? "grayscale opacity-50" : ""
              }`}
              onClick={() => {
                stockiestPortalController.toggleBasketDrawerOpen(false);
                issueDetailsController.setMagazineHint(issue.magazineId);
                navigate(`/issue/${issue.id}`);
              }}
            />
          ) : (
            <div className="relative w-full h-full flex flex-col items-center justify-center">
              {issue.previousIssuesImageURLs.length > 0 ?
                <>
                  {(issue.previousIssuesImageURLs.slice(0, 1)).map((url, i) => {
                    const positions = [
                      "top-0 left-0 z-10 rotate-0",
                      "top-[-0.5rem] left-[0.5rem] z-20 rotate-2",
                      "top-[-1rem] left-[1rem] z-30 rotate-3"
                    ];

                    return (
                      <img
                        key={url + i}
                        src={url}
                        alt={`Previous Issue ${i + 1}`}
                        className={`absolute rounded-md object-cover shadow-lg brightness-[50%] grayscale-[50%] w-[120px] h-[150px] outline outline-1 outline-white cursor-pointer ${positions[i] ?? "top-0 left-0"}`}
                      />
                    );
                  })
                  }
                  <span
                    className={`absolute text-white/60 font-bold select-none pointer-events-none z-30 -translate-x-1/2 -translate-y-1/2 top-1/2 left-1/2 rotate-[-45deg] uppercase text-lg w-full text-center`}>Sample Cover</span>
                </> :
                <div
                  title="No cover available"
                  className="flex flex-col w-[120px] h-[150px] items-center justify-center rounded-md bg-main outline outline-1 outline-light-grey/50 object-cover shadow-sm transition-all duration-300 cursor-pointer"
                >
                  <span className="text-white italic text-xl font-bold text-center">NEW LAUNCH!</span>
                </div>
              }
            </div>
          )}
          {(issue.issueOnSale <= new Date() && stock.stockAvailableForStockist <= 0) && (
            <div
              className="absolute top-2 right-0 bg-black text-white text-sm font-semibold px-3 py-1 rounded shadow border border-white rotate-[10deg]">
              No longer in stock
            </div>
          )}
        </div>
      </div>

      <div className="flex-1 flex flex-col justify-between w-full">
        <div className="flex flex-col">
          <h2 className="flex items-center text-white font-semibold">
            <Link
              className="transition duration-150 ease-in text-white hover:text-gray-400 cursor-pointer truncate block max-w-[140px]"
              title={`See latest issues of ${issue.magazine.titleDisplay}`}
              onClick={() => { stockiestPortalController.toggleBasketDrawerOpen(false); issueDetailsController.setMagazineHint(issue.magazineId); }}
              to={issue.magazine.stockistMagazineUrl}
            >
              {issue.magazine.titleDisplay}
            </Link>

            <IconButton
              size="sm"
              className="ml-1 mb-0.5 h-4 w-4 text-red-400 hover:text-red-600 transition"
              disabled={orderedProduct.isUpdatingQuantity}
              onClick={() => stockiestPortalController.removeOrderedProduct(orderedProduct)}
            >
              <TrashIcon className="h-4 w-4"/>
            </IconButton>
          </h2>
          <h3
            className={`text-white text-sm italic ${(issue.issueOnSale <= new Date() && stock.stockAvailableForStockist <= 0) ? "opacity-70 italic" : ""}`}>{issue.identity}</h3>
          {issue.issueOnSale > new Date() &&
            <div className="flex items-center gap-1 mt-2">
              <Typography className="text-gray-300 text-right text-sm">{issue.onSaleDateDisplay}</Typography>
              <Tooltip content="Estimated on-sale date">
                <InformationCircleIcon className="h-4 w-4 text-gray-300 cursor-pointer"/>
              </Tooltip>
            </div>
          }
        </div>

        {!(issue.issueOnSale <= new Date() && stock.stockAvailableForStockist <= 0) ? (
          <>
            <div className="flex items-center mt-1">
              <Button
                variant="outlined"
                color="red"
                size="sm"
                onClick={() => changeOrderAmount(-1, true)}
                className="h-10 !border-light-grey/50 !text-white text-md hover:!bg-red-500 hover:!border-red-500 transition rounded-r-none"
                disabled={orderedProduct.isUpdatingQuantity || quantity <= 0}
              >
                −
              </Button>

              <input
                type="number"
                name="quantity"
                min={1}
                max={orderedProduct.maxAvailableQuantity}
                value={inputValue}
                onChange={handleInputChange}
                onBlur={handleBlur}
                className="w-12 h-10 text-center bg-gray-700 text-white border border-gray-600 focus:outline-none !appearance-none rounded-none"
                disabled={orderedProduct.isUpdatingQuantity}
              />

              <Button
                variant="outlined"
                color="green"
                size="sm"
                onClick={() => changeOrderAmount(1, true)}
                className="h-10 !border-light-grey/50 !text-white text-md hover:!bg-green-500 hover:!border-green-500 transition rounded-l-none"
                disabled={
                  orderedProduct.isUpdatingQuantity ||
                  quantity >= orderedProduct.maxAvailableQuantity
                }
              >
                +
              </Button>
            </div>
            {orderedProduct.isFreebieEligible && orderedProduct.freebiesDue > 0 && (
              <span className="text-xs font-bold text-amber-300 mt-1">
                🎁 +{orderedProduct.freebiesDue} free
              </span>
            )}
          </>
        ) : (
          <Button
            variant="outlined"
            color="red"
            size="sm"
            onClick={removeItem}
            className="mt-3 h-10 w-full !border-red-400 !text-red-400 hover:!bg-red-600 hover:!text-white transition"
          >
            Remove
          </Button>
        )}
        {((issue.issueOnSale <= new Date()) && (stock.stockAvailableForStockist > 0)) ? (
          <div className="mt-2 flex items-baseline">
            <AnimatePresence mode="popLayout">
              <motion.div
                key="price"
                initial={{opacity: 0}}
                animate={{opacity: 1}}
                exit={{opacity: 0}}
                transition={{duration: 0.15}}
                className="flex items-baseline"
              >
                {sortedDiscounts && sortedDiscounts.length > 0 ? (
                  <>
                    <div className="flex flex-col items-baseline">
                      <span className="text-gray-500 line-through text-sm">
                        £{baseTotal.toFixed(2)}
                      </span>

                      <motion.span
                        key={discountedTotal}
                        initial={{opacity: 0.3, y: -3}}
                        animate={{opacity: 1, y: 0}}
                        exit={{opacity: 0, y: 3}}
                        transition={{duration: 0.2}}
                        className="text-white font-semibold text-xl"
                      >
                        £{discountedTotal.toFixed(2)}
                      </motion.span>
                    </div>

                    {discountPercentage > 0 && (
                      <span className="bg-green-400 text-sm text-white px-1 py-0.5 rounded">
                        -{discountPercentage}%
                      </span>
                    )}
                  </>
                ) : (
                  <motion.span
                    key={baseTotal}
                    initial={{opacity: 0.3, y: -3}}
                    animate={{opacity: 1, y: 0}}
                    exit={{opacity: 0, y: 3}}
                    transition={{duration: 0.2}}
                    className={`text-white font-semibold text-xl ${(issue.issueOnSale <= new Date() && stock.stockAvailableForStockist <= 0) ? "opacity-70" : ""}`}
                  >
                    £{baseTotal.toFixed(2)}
                  </motion.span>
                )}
              </motion.div>
            </AnimatePresence>
          </div>
        ) : (
          <div className="mt-4 flex items-baseline">
            <span
              className={`text-white font-semibold text-xl ${(issue.issueOnSale <= new Date() && stock.stockAvailableForStockist <= 0) ? "opacity-70" : ""}`}>
              RRP {issue.priceDisplay}
            </span>
          </div>
        )
        }
      </div>
    </div>
  );
});

